import { Routes, Route, Navigate, useParams } from 'react-router-dom';
import { Box } from '@mui/material';
import { motion } from 'framer-motion';

// Components
import Navbar from './components/layout/Navbar';
import Sidebar from './components/layout/Sidebar';
import LoadingSpinner from './components/common/LoadingSpinner';

// Pages
import Dashboard from './pages/Dashboard';
import Appointments from './pages/Appointments';
import Staff from './pages/Staff';
import Services from './pages/Services';
import Stores from './pages/Stores';
import Networks from './pages/Networks';
import Schedule from './pages/Schedule';
import Login from './pages/Login';
import BookingFlow from './pages/BookingFlow';
import StoreDetail from './pages/StoreDetail';
import StoreEdit from './pages/StoreEdit';
import PaymentSetup from './pages/PaymentSetup';
import ManageSeats from './pages/ManageSeats';
import MyProfile from './pages/MyProfile';
import PlatformSubscription from './pages/PlatformSubscription';
import StripeOnboardingReturn from './pages/StripeOnboardingReturn';
import TestPage from './pages/TestPage';
import ModalDemo from './pages/ModalDemo';

// Hooks
import { useAuth } from './contexts/AuthContext';
import { useState } from 'react';
import { useMediaQuery, useTheme } from '@mui/material';
import { useQuery } from '@apollo/client';
import { GET_STORE_BY_ID } from './graphql/queries';

// Guard: redirects to /subscription if the store's platform subscription is
// not active or trialing. Payments and Subscription pages are always reachable.
const ACTIVE_STATUSES = ['active', 'trialing'];

function SubscriptionGuard({ element }: { element: React.ReactElement }) {
  const { storeId } = useParams<{ storeId: string }>();
  const { data } = useQuery(GET_STORE_BY_ID, {
    variables: { storeId },
    skip: !storeId,
    fetchPolicy: 'cache-first',
  });
  const status: string | undefined = data?.storeDetails?.platformSubscriptionStatus;

  // While status is still loading (undefined) let the page render — avoids
  // a flash redirect before we know the actual status.
  if (status && !ACTIVE_STATUSES.includes(status)) {
    return <Navigate to={`/store/${storeId}/subscription`} replace />;
  }
  return element;
}

function App() {
  const { isAuthenticated, loading } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleMenuClick = () => {
    setSidebarOpen(!sidebarOpen);
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!isAuthenticated) {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/book/:storeId" element={<BookingFlow />} />
        <Route path="/store/:storeId" element={<StoreDetail />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <Box sx={{
        flexGrow: 1,
        display: 'flex',
        flexDirection: 'column',
        width: isMobile ? '100%' : 'auto',
      }}>
        <Navbar onMenuClick={handleMenuClick} />

        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: { xs: 1.5, sm: 2, md: 3 },
            pt: {
              xs: 'calc(72px + env(safe-area-inset-top))',
              sm: 'calc(80px + env(safe-area-inset-top))',
              md: 'calc(88px + env(safe-area-inset-top))',
            },
            backgroundColor: 'background.default',
            minHeight: 'calc(100vh - 64px)',
            transition: 'margin-left 0.3s ease',
          }}
        >
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Routes>
              {/* Store-specific routes — gated behind platform subscription */}
              <Route path="/store/:storeId/dashboard" element={<SubscriptionGuard element={<Dashboard />} />} />
              <Route path="/store/:storeId/appointments" element={<SubscriptionGuard element={<Appointments />} />} />
              <Route path="/store/:storeId/staff" element={<SubscriptionGuard element={<Staff />} />} />
              <Route path="/store/:storeId/services" element={<SubscriptionGuard element={<Services />} />} />
              <Route path="/store/:storeId/schedule" element={<SubscriptionGuard element={<Schedule />} />} />
              <Route path="/store/:storeId/details" element={<SubscriptionGuard element={<StoreDetail />} />} />
              <Route path="/store/:storeId/edit" element={<SubscriptionGuard element={<StoreEdit />} />} />
              {/* Payments and Subscription are always reachable so stores can pay to unlock */}
              <Route path="/store/:storeId/payments" element={<PaymentSetup />} />
              <Route path="/store/:storeId/subscription" element={<PlatformSubscription />} />
              <Route path="/store/:storeId/seats" element={<ManageSeats />} />
              <Route path="/my-profile" element={<MyProfile />} />
              
              {/* Global routes */}
              <Route path="/stores" element={<Stores />} />
              <Route path="/networks" element={<Networks />} />
              <Route path="/test" element={<TestPage />} />
              <Route path="/modal-demo" element={<ModalDemo />} />
              
              {/* Stripe onboarding return/refresh routes */}
              <Route path="/stripe/onboarding/return" element={<StripeOnboardingReturn />} />
              <Route path="/stripe/onboarding/refresh" element={<StripeOnboardingReturn />} />

              {/* Public booking route */}
              <Route path="/book/:storeId" element={<BookingFlow />} />
              
              {/* Default redirects */}
              <Route path="/" element={<Navigate to="/stores" replace />} />
              <Route path="/dashboard" element={<Navigate to="/stores" replace />} />
              <Route path="/store/:storeId" element={<Navigate to="/store/:storeId/dashboard" replace />} />
              <Route path="*" element={<Navigate to="/stores" replace />} />
            </Routes>
          </motion.div>
        </Box>
      </Box>
    </Box>
  );
}

export default App;
